import React, { useState } from "react";
import { maintenanceApi, ProcOrderDetail, ProcReceiptResult } from "../../../api/maintenance";
import { CONDITIONS, errMsg } from "./procUtils";
import { useNotice } from "../shared/useNotice";

interface Props {
  order: ProcOrderDetail;
  onClose: () => void;
  onDone: (res: ProcReceiptResult) => void;
}

// "skip" is not a condition — it means the unit simply wasn't in this shipment,
// so it keeps no receipt line and stays outstanding for the next one.
type Choice = "ok" | "damaged" | "doa" | "wrong_item" | "skip";

/** Record ONE shipment against an order, item by item. */
const ReceiptForm: React.FC<Props> = ({ order, onClose, onDone }) => {
  const outstanding = order.items.filter((i) => i.condition === null);

  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));
  const [challan, setChallan] = useState("");
  const [note, setNote] = useState("");
  const [choices, setChoices] = useState<Record<number, Choice>>(
    Object.fromEntries(outstanding.map((i) => [i.procurement_device_id, "skip" as Choice]))
  );
  const [lineNotes, setLineNotes] = useState<Record<number, string>>({});
  const [error, setError] = useNotice("error", "");
  const [saving, setSaving] = useState(false);

  const setAll = (c: Choice) =>
    setChoices(Object.fromEntries(outstanding.map((i) => [i.procurement_device_id, c])));

  const arriving = outstanding.filter((i) => choices[i.procurement_device_id] !== "skip");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (arriving.length === 0) {
      setError("Nothing marked as arrived — pick at least one unit, or cancel.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res = await maintenanceApi.procCreateReceipt(order.id, {
        received_on: receivedOn,
        challan_ref: challan.trim() || undefined,
        note: note.trim() || undefined,
        lines: arriving.map((i) => ({
          procurement_device_id: i.procurement_device_id,
          condition: choices[i.procurement_device_id],
          note: lineNotes[i.procurement_device_id]?.trim() || undefined,
        })),
      });
      onDone(res.data);
    } catch (err: any) {
      setError(errMsg(err, "Failed to record receipt"));
      setSaving(false);
    }
  };

  return (
    <div className="maint-form-overlay" onClick={onClose}>
      <div className="maint-form-card maint-form-card--widest" onClick={(e) => e.stopPropagation()}>
        <div className="maint-form-header">
          <h3>Record a receipt against {order.order_no}</h3>
          <button className="maint-btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>

        <p className="proc-mini" style={{ marginTop: 0 }}>
          Shipments arrive in dribs and drabs. Record <b>only what turned up today</b> — leave the
          rest as “not in this shipment” and they stay outstanding for the next one. Damaged / DOA
          units are retired with an RMA note and can never reach the onboarding picker.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="maint-form-row">
            <label>
              Received on
              <input type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
            </label>
            <label>
              Challan / invoice ref
              <input value={challan} onChange={(e) => setChallan(e.target.value)} placeholder="EXI/DC/…" />
            </label>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "6px 0 8px",
            }}
          >
            <b style={{ fontSize: 13, color: "#555" }}>
              {outstanding.length} unit{outstanding.length === 1 ? "" : "s"} outstanding ·{" "}
              <span style={{ color: "#2e7d32" }}>{arriving.length} in this shipment</span>
            </b>
            <span style={{ display: "flex", gap: 6 }}>
              <button type="button" className="maint-btn-small" onClick={() => setAll("ok")}>
                Mark all OK
              </button>
              <button type="button" className="maint-btn-small" onClick={() => setAll("skip")}>
                Clear
              </button>
            </span>
          </div>

          <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 8 }}>
            <table className="maint-table" style={{ boxShadow: "none" }}>
              <thead>
                <tr>
                  <th>Device</th>
                  <th style={{ width: 200 }}>Condition</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {outstanding.map((i) => {
                  const c = choices[i.procurement_device_id];
                  return (
                    <tr key={i.procurement_device_id}>
                      <td className="proc-mono">{i.unique_id}</td>
                      <td>
                        <select
                          value={c}
                          onChange={(e) =>
                            setChoices((prev) => ({
                              ...prev,
                              [i.procurement_device_id]: e.target.value as Choice,
                            }))
                          }
                          style={{
                            width: "100%",
                            padding: "5px 8px",
                            border: "1px solid #e0e0e0",
                            borderRadius: 6,
                            fontSize: 13,
                          }}
                        >
                          <option value="skip">— not in this shipment</option>
                          {CONDITIONS.map((x) => (
                            <option key={x.key} value={x.key}>
                              {x.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {c !== "skip" && c !== "ok" && (
                          <input
                            value={lineNotes[i.procurement_device_id] || ""}
                            onChange={(e) =>
                              setLineNotes((prev) => ({
                                ...prev,
                                [i.procurement_device_id]: e.target.value,
                              }))
                            }
                            placeholder="what's wrong with it?"
                            style={{
                              width: "100%",
                              padding: "5px 8px",
                              border: "1px solid #e0e0e0",
                              borderRadius: 6,
                              fontSize: 13,
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <label style={{ marginTop: 12 }}>
            Note (optional)
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. first truck" />
          </label>

          {error && <p className="maint-form-error">{error}</p>}

          <div className="maint-form-actions">
            <button type="button" className="maint-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="maint-btn-primary" disabled={saving}>
              {saving ? "Recording…" : `Record receipt (${arriving.length})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReceiptForm;
